import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Text } from '@atlaskit/primitives';
import { AnalyticsEventActions, AnalyticsEventSource, useAnalytics } from '@atlassian/ptc-common';

import Anchor from '../Anchor';
import { useAppConfig } from '../AppConfigContext';

interface Props {
	isInEmptyState?: boolean;
	// at this moment, we just support rendering SearchMoreWork in user first, not team now.
	userId: string;
	userFullName: string;
	analyticAttributes?: {
		source: AnalyticsEventSource;
	};
}

const getConfluenceSearchLink = (userId: string) => {
	return `/wiki/search?contributors=${encodeURIComponent(userId)}&lastModified=lastMonth`;
};

const getJiraSearchLink = (userId: string) => {
	// not sure why Jira does not need context-path for issue search page
	const jql =
		`assignee in (${userId})` +
		` OR reporter in (${userId})` +
		` OR creator in (${userId})` +
		` OR watcher in (${userId})` +
		' ORDER BY updated DESC';

	return `/issues/?jql=${encodeURIComponent(jql)}`;
};

const SearchLink = (props: { container: 'Confluence' | 'Jira'; userId: string }) => {
	const analytics = useAnalytics();
	const { siteUrl } = useAppConfig();

	const { container, userId } = props;

	const onClickSearchLink = () => {
		analytics.pushUIEvent({
			source: AnalyticsEventSource.USER_WORK_SCREEN,
			actionSubject: 'searchMoreLink',
			action: AnalyticsEventActions.CLICKED,
			attributes: {
				container,
			},
		});
	};

	return (
		<Anchor
			href={
				fg('append_site_url_more_search')
					? `${siteUrl}${container === 'Confluence' ? getConfluenceSearchLink(userId) : getJiraSearchLink(userId)}`
					: container === 'Confluence'
						? getConfluenceSearchLink(userId)
						: getJiraSearchLink(userId)
			}
			target="_blank"
			data-test-selector={container.toLowerCase()}
			onClick={onClickSearchLink}
		>
			{container}
		</Anchor>
	);
};

const SearchMoreWork = (props: Props) => {
	const { isInEmptyState, userId, userFullName } = props;

	if (!userId || !userFullName) {
		return null;
	}

	return (
		<Text as="p">
			{isInEmptyState ? (
				<FormattedMessage {...messages.searchInEmptyState} />
			) : (
				<FormattedMessage {...messages.searchNotInEmptyState} />
			)}{' '}
			<SearchLink container="Confluence" userId={userId} /> <FormattedMessage {...messages.or} />{' '}
			<SearchLink container="Jira" userId={userId} />
		</Text>
	);
};

const messages = defineMessages({
	searchInEmptyState: {
		id: 'ptc-directory.profile-page.work-list.search.in.empty.state',
		defaultMessage: 'You can also search in',
		description:
			'A part of a full message is which "You can also search in Confluence or Jira". This is used in empty state of work list component',
	},
	searchNotInEmptyState: {
		id: 'ptc-directory.profile-page.work-list.search.in.not.empty.state',
		defaultMessage: 'Search for more in',
		description:
			'A part of a full message is which "Search for more in in Confluence or Jira". This is used in empty state of work list table component',
	},
	or: {
		id: 'ptc-directory.profile-page.work-list.or',
		defaultMessage: 'or',
		description: '',
	},
});

export default SearchMoreWork;
